import { observable } from "@legendapp/state";
import { getPerformanceTableReport } from "@/apis/clients/reportingClient";
import {
  createRequestGuard,
  reportScopeKey,
  toErrorMessage,
} from "@/stores/storeHelpers";
import type { PerformanceReportRow, ReportScope } from "@/types/reporting";

interface PerformanceTableState {
  rows: PerformanceReportRow[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
}

const defaultValues: PerformanceTableState = {
  rows: [],
  isLoading: true,
  error: null,
  loadedForScopeKey: null,
};

export const PerformanceTableStore$ = observable(defaultValues);

const getScopeKey = (scope: ReportScope) => `table:${reportScopeKey(scope)}`;

const guard = createRequestGuard();

export const fetchPerformanceTable = async (scope: ReportScope) => {
  const scopeKey = getScopeKey(scope);
  const result = guard.check(
    scopeKey,
    PerformanceTableStore$.loadedForScopeKey.get()
  );
  if (result.outcome === "cached") {
    PerformanceTableStore$.assign({ isLoading: false, error: null });
    return;
  }
  if (result.outcome === "in-flight") return;
  const { requestId } = result;
  PerformanceTableStore$.assign({ isLoading: true, error: null });

  try {
    const response = await getPerformanceTableReport(scope);
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    PerformanceTableStore$.assign({
      rows: response.results,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    PerformanceTableStore$.error.set(toErrorMessage(error));
  } finally {
    if (!guard.isStale(requestId)) {
      PerformanceTableStore$.isLoading.set(false);
    }
  }
};

export const clearPerformanceTable = () => {
  guard.invalidate();
  PerformanceTableStore$.assign({
    error: null,
    isLoading: false,
    loadedForScopeKey: null,
    rows: [],
  });
};
